import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const Portfolio: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  const projects = [
    {
      id: 1,
      title: 'Indian Suraksha Force',
      category: 'html',
      type: 'HTML Website',
      description: 'Private security service agency website created to establish a professional digital identity and connect with clients across border regions.',
      tech: ['HTML5', 'CSS3', 'JavaScript', 'XML', 'robots.txt', '.htaccess'],
      image: '/project_screenshot/ISF.png',
      link: 'https://indiansurakshaforce.com/',
      features: ['Security Services Showcase', 'Digital Business Identity', 'SEO Essentials', 'Responsive Design']
    },
    {
      id: 2,
      title: 'Savitur Rockland School',
      category: 'wordpress',
      type: 'WordPress Website',
      description: 'Complete school management system with admin dashboard, student portal, and content management.',
      tech: ['WordPress', 'PHP', 'MySQL', 'Bootstrap'],
      image: '/project_screenshot/savitur_rockland.png',
      link: 'https://saviturrockland.com',
      features: ['Admin Dashboard', 'Student Management', 'Content Management', 'Responsive Design']
    },
    {
      id: 3,
      title: 'Barak Bani News',
      category: 'wordpress',
      type: 'WordPress Website',
      description: 'Modern news portal with custom themes, article management, and media integration.',
      tech: ['WordPress', 'PHP', 'MySQL', 'Custom Theme'],
      image: '/project_screenshot/epaper.barakbaninews.com.png',
      link: 'https://barakbaninews.com',
      features: ['News Management', 'Media Gallery', 'SEO Optimized', 'Mobile Responsive']
    },
    {
      id: 4,
      title: 'Terve Life Sciences',
      category: 'php',
      type: 'PHP Website',
      description: 'Professional business website for life sciences company with modern UI and contact forms.',
      tech: ['PHP', 'HTML5', 'CSS3', 'JavaScript'],
      image: '/project_screenshot/terve_lifescience.png',
      link: 'https://tervelifesciences.in',
      features: ['Modern UI', 'Contact Forms', 'Service Showcase', 'SEO Friendly']
    },
    {
      id: 5,
      title: 'SDDKB Mission School',
      category: 'php',
      type: 'PHP Website',
      description: 'Educational institution website with student information system and management features.',
      tech: ['PHP', 'MySQL', 'Bootstrap', 'JavaScript'],
      image: '/project_screenshot/swami_dhananjoy_das_kathia_baba_mission_school.png',
      link: 'https://sddkbmissionschool.com',
      features: ['Student Portal', 'Information System', 'Management Tools', 'Responsive Design']
    },
    {
      id: 6,
      title: 'Konscious Lifestyle',
      category: 'wordpress',
      type: 'E-commerce Website',
      description: 'Complete e-commerce platform for lifestyle products with payment integration.',
      tech: ['WordPress', 'WooCommerce', 'PHP', 'Stripe'],
      image: '/project_screenshot/konscious_lifestyle.png',
      link: 'https://konsciouslifestyle.in',
      features: ['E-commerce', 'Payment Integration', 'Product Management', 'Order Processing']
    },
    {
      id: 7,
      title: 'Epaper Barak Bani News',
      category: 'nodejs',
      type: 'Node.js Application',
      description: 'Digital newspaper application with user authentication and content management.',
      tech: ['Node.js', 'Express.js', 'MongoDB', 'EJS'],
      image: '/project_screenshot/epaper.barakbaninews.com.png',
      link: 'https://epaper.barakbaninews.com',
      features: ['User Authentication', 'Content Management', 'Digital Publishing', 'Responsive UI']
    }
  ];

  const filters = [
    { id: 'all', label: 'All Projects', count: projects.length },
    { id: 'html', label: 'HTML/CSS/JS', count: projects.filter(p => p.category === 'html').length },
    { id: 'wordpress', label: 'WordPress', count: projects.filter(p => p.category === 'wordpress').length },
    { id: 'nodejs', label: 'Node.js', count: projects.filter(p => p.category === 'nodejs').length },
    { id: 'php', label: 'PHP', count: projects.filter(p => p.category === 'php').length }
  ];

  const filteredProjects = activeFilter === 'all'
    ? projects
    : projects.filter(project => project.category === activeFilter);

  return (
    <div className="portfolio-page">
      {/* Hero Section */}
      <section className="portfolio-hero">
        <motion.div
          className="hero-content"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1>Our Portfolio</h1>
          <p className="hero-subtitle">
            Showcasing our expertise through real projects and successful digital solutions
          </p>
        </motion.div>
        <div className="hero-bg">
          <div className="floating-elements">
            <div className="floating-orb orb-1"></div>
            <div className="floating-orb orb-2"></div>
            <div className="floating-orb orb-3"></div>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="portfolio-filters">
        <div className="container">
          <motion.div
            className="filter-buttons"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {filters.map(filter => (
              <button
                key={filter.id}
                className={`filter-btn ${activeFilter === filter.id ? 'active' : ''}`}
                onClick={() => setActiveFilter(filter.id)}
              >
                {filter.label}
                <span className="filter-count">({filter.count})</span>
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="projects-section">
        <div className="container">
          <motion.div
            className="projects-grid"
            layout
          >
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                className="project-card-premium"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                layout
              >
                <div className="project-image-container">
                  <img
                    src={process.env.PUBLIC_URL + project.image}
                    alt={`${project.title} screenshot`}
                    className="project-image"
                    draggable="false"
                    onContextMenu={(e) => e.preventDefault()}
                  />
                  <div className="project-overlay">
                    <div className="project-links">
                      <a
                        href={project.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="view-live-btn"
                      >
                        View Live
                      </a>
                    </div>
                  </div>
                </div>

                <div className="project-content">
                  <div className="project-header">
                    <h3>{project.title}</h3>
                    <span className="project-type">{project.type}</span>
                  </div>

                  <p className="project-description">{project.description}</p>

                  <div className="project-features">
                    <h4>Key Features:</h4>
                    <div className="features-list">
                      {project.features.map((feature, i) => (
                        <span key={i} className="feature-tag">{feature}</span>
                      ))}
                    </div>
                  </div>

                  <div className="project-tech">
                    <h4>Technologies:</h4>
                    <div className="tech-stack">
                      {project.tech.map((tech, i) => (
                        <span key={i} className="tech-badge">{tech}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="portfolio-stats">
        <motion.div
          className="container"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-number">15+</div>
              <div className="stat-label">Projects Completed</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">100%</div>
              <div className="stat-label">Responsive Solutions</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">15+</div>
              <div className="stat-label">Technologies Used</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">99%</div>
              <div className="stat-label">Client Satisfaction</div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* CTA Section */}
      <section className="portfolio-cta">
        <motion.div
          className="container"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2>Ready to Start Your Project?</h2>
          <p>
            Let's create something amazing together. Contact us to discuss your vision and requirements.
          </p>
          <div className="cta-buttons">
            <Link to="/contact" className="btn-primary">Start Your Project</Link>
            <Link to="/services" className="btn-secondary">View Services</Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default Portfolio;
